'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Bell, ExternalLink, Loader2, CheckCircle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface FollowForm {
  nombre: string;
  email: string;
  notify: boolean;
}

export default function FollowCTA() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FollowForm>({ defaultValues: { notify: true } });

  const onSubmit = async (data: FollowForm) => {
    // BACKEND: POST /api/followers — save follower with email, nombre, notify flag
    await new Promise((r) => setTimeout(r, 1000));
    setSubmitted(true);
  };

  return (
    <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-20">
      <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background border border-primary/20 rounded-3xl p-10 lg:p-16 text-center">
        <div className="text-5xl mb-4">🔔</div>
        <h2 className="text-3xl font-bold text-foreground mb-3">
          No te pierdas ningún live
        </h2>
        <p className="text-muted-foreground text-base max-w-lg mx-auto mb-8">
          Regístrate y te avisamos cuando la anfi esté en vivo. ¡Gratis, sin spam!
        </p>

        {submitted ? (
          <div className="inline-flex items-center gap-3 bg-green-100 text-green-700 border border-green-200 rounded-2xl px-8 py-5 text-base font-semibold">
            <CheckCircle size={22} />
            ¡Listo! Te avisaremos cuando haya un live 🧶
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="max-w-md mx-auto flex flex-col gap-4">
            <div className="text-left">
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Tu nombre (opcional)
              </label>
              <input
                {...register('nombre')}
                type="text"
                placeholder="¿Cómo te llamamos?"
                className="w-full bg-card border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
              />
            </div>

            <div className="text-left">
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Tu email <span className="text-primary">*</span>
              </label>
              <input
                {...register('email', {
                  required: 'El email es obligatorio',
                  pattern: { value: /\S+@\S+\.\S+/, message: 'Email inválido' },
                })}
                type="email"
                placeholder="tucorreo@gmail.com"
                className={`w-full bg-card border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
                  errors.email ? 'border-red-400' : 'border-border'
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer text-left">
              <input
                {...register('notify')}
                type="checkbox"
                className="mt-0.5 w-4 h-4 rounded accent-primary"
              />
              <span className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">Avísame cuando haya un live</span> — te mandamos un email cuando la anfi esté transmitiendo 🧶
              </span>
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold text-base disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <Bell size={18} />
                  Seguir el Canal 🧶
                </>
              )}
            </button>
          </form>
        )}

        {/* Social buttons */}
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <span className="text-xs text-muted-foreground font-medium">También en:</span>
          {[
            { label: 'Instagram', icon: ExternalLink, color: 'hover:bg-pink-100 hover:text-pink-600' },
            { label: 'YouTube', icon: ExternalLink, color: 'hover:bg-red-100 hover:text-red-600' },
            { label: 'TikTok', icon: ExternalLink, color: 'hover:bg-gray-100 hover:text-gray-700' },
          ].map((social) => {
            const Icon = social.icon;
            return (
              <button
                key={`social-${social.label}`}
                className={`flex items-center gap-2 bg-card border border-border px-4 py-2.5 rounded-xl text-sm font-medium text-muted-foreground transition-all ${social.color}`}
              >
                <Icon size={16} />
                {social.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}